import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";

/**
 * FormCarousel — generic multi-step form modal.
 *
 * Reduces big creation forms into 2–4 focused steps. Step 1 holds only the
 * minimum required to save a record; once it validates, the user gets a
 * "Save and finish later" escape hatch alongside "Continue →". State lives
 * in the parent component (this), so back-navigation never loses data.
 *
 * Usage:
 *
 *   <FormCarousel
 *     title="New Brief"
 *     draftKey="brief"
 *     initialState={{ title: "", lead_id: "", ... }}
 *     steps={[
 *       {
 *         key: "essentials",
 *         label: "Essentials",
 *         validate: (form) => {
 *           const errs = {};
 *           if (!form.title?.trim()) errs.title = "Title required";
 *           ...
 *           return errs;          // empty obj = valid
 *         },
 *         render: ({ form, setForm, errors }) => <EssentialsStep ... />,
 *       },
 *       { key: "schedule", label: "Schedule", optional: true, render: ... },
 *     ]}
 *     onSaveMinimum={async (form) => { await api.post("/x", minimalPayload); }}
 *     onSaveFull={async (form) => { await api.post("/x", form); }}
 *     onClose={() => setShowAdd(false)}
 *   />
 *
 * Props:
 *   - title           string — modal title
 *   - draftKey        string — localStorage key suffix for autosave (`dm_draft_<key>`)
 *   - initialState    object — starting form values (merged over autosaved draft)
 *   - steps           [{key, label, optional?, validate?, render}]
 *   - onSaveMinimum   async (form) => void — called by "Save and finish later" once step 1 validates
 *   - onSaveFull      async (form) => void — called when user reaches the final step's primary button
 *   - onClose         () => void — close the modal
 *   - finalLabel      string — primary button label on last step (default: "Create")
 */
export default function FormCarousel({
  title,
  draftKey,
  initialState = {},
  steps = [],
  onSaveMinimum,
  onSaveFull,
  onClose,
  finalLabel = "Create",
}) {
  const storageKey = `dm_draft_${draftKey}`;

  // Hydrate from autosaved draft if present.
  const initial = useMemo(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const draft = JSON.parse(raw);
        return { ...initialState, ...draft };
      }
    } catch {}
    return initialState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const [form, setForm] = useState(initial);
  const [stepIdx, setStepIdx] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Autosave draft on every change.
  useEffect(() => {
    if (!dirty) return;
    try { localStorage.setItem(storageKey, JSON.stringify(form)); } catch {}
  }, [form, storageKey, dirty]);

  // Esc closes (with a confirm if dirty).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") attemptClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const setFormAndDirty = (updater) => {
    setDirty(true);
    setForm((prev) => (typeof updater === "function" ? updater(prev) : { ...prev, ...updater }));
  };

  const validateStep = (idx) => {
    const step = steps[idx];
    if (!step?.validate) return {};
    return step.validate(form) || {};
  };

  const isStep1Valid = useMemo(() => Object.keys(validateStep(0)).length === 0, [form, steps]);

  const attemptClose = () => {
    if (dirty) {
      const ok = window.confirm("Discard your changes? Your progress will be lost.");
      if (!ok) return;
    }
    try { localStorage.removeItem(storageKey); } catch {}
    onClose?.();
  };

  const goNext = () => {
    const errs = validateStep(stepIdx);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const first = Object.values(errs)[0];
      if (first) toast.error(first);
      return;
    }
    setErrors({});
    if (stepIdx < steps.length - 1) {
      setStepIdx(stepIdx + 1);
    }
  };

  const goPrev = () => {
    setErrors({});
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const jumpTo = (idx) => {
    if (idx === stepIdx) return;
    if (idx < stepIdx) {
      // Always allow jumping back.
      setErrors({});
      setStepIdx(idx);
      return;
    }
    // Forward jump only if every step from current up to (but not including) target is valid.
    for (let i = stepIdx; i < idx; i++) {
      const e = validateStep(i);
      if (Object.keys(e).length > 0) {
        setErrors(e);
        const first = Object.values(e)[0];
        if (first) toast.error(first);
        return;
      }
    }
    setErrors({});
    setStepIdx(idx);
  };

  const handleSaveMinimum = async () => {
    const errs = validateStep(0);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setStepIdx(0);
      return;
    }
    setSubmitting(true);
    try {
      await onSaveMinimum?.(form);
      try { localStorage.removeItem(storageKey); } catch {}
    } catch (e) {
      // onSaveMinimum is responsible for surfacing toast errors; rethrow to skip cleanup
      console.error("FormCarousel.saveMinimum failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveFull = async () => {
    // Validate the current (final) step. Earlier steps have been validated as the user advanced.
    const errs = validateStep(stepIdx);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const first = Object.values(errs)[0];
      if (first) toast.error(first);
      return;
    }
    setSubmitting(true);
    try {
      await onSaveFull?.(form);
      try { localStorage.removeItem(storageKey); } catch {}
    } catch (e) {
      console.error("FormCarousel.saveFull failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  const isLastStep = stepIdx === steps.length - 1;
  const currentStep = steps[stepIdx];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(0,0,0,.7)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) attemptClose(); }}
    >
      <div
        className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl w-full max-w-[min(95vw,640px)] my-8 max-h-[90vh] flex flex-col"
        style={{ boxShadow: "0 24px 80px rgba(0,0,0,.6)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b border-[var(--bdr)]">
          <div>
            <h2 className="text-[16px] font-bold text-white font-heading">{title}</h2>
            <p className="text-[11px] text-[#888] mt-0.5">
              Step {stepIdx + 1} of {steps.length}
              {currentStep?.optional ? " · optional" : ""}
            </p>
          </div>
          <button
            onClick={attemptClose}
            className="text-[#555] hover:text-red-400 hover:bg-red-500/10 transition-all p-1.5 rounded-lg"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Step pills */}
        <div className="px-5 pt-3 pb-3 border-b border-[var(--bdr)] overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          <div className="flex items-center gap-1.5">
            {steps.map((s, i) => {
              const completed = i < stepIdx;
              const active = i === stepIdx;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => jumpTo(i)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors"
                  style={{
                    background: active ? "var(--r)" : completed ? "rgba(34,197,94,.15)" : "var(--s2)",
                    color: active ? "white" : completed ? "#22C55E" : "var(--t3)",
                    border: `1px solid ${active ? "var(--r)" : completed ? "rgba(34,197,94,.3)" : "var(--bdr)"}`,
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: completed ? "rgba(34,197,94,.3)" : active ? "rgba(255,255,255,.25)" : "var(--s3)",
                      fontSize: 9,
                    }}
                  >
                    {completed ? <Check size={9} strokeWidth={3} /> : i + 1}
                  </span>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {currentStep?.render?.({ form, setForm: setFormAndDirty, errors, setErrors, goNext, goPrev })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--bdr)] flex items-center gap-2 bg-[var(--s1)] rounded-b-2xl">
          <button
            type="button"
            onClick={goPrev}
            disabled={stepIdx === 0 || submitting}
            className="flex items-center gap-1.5 px-3 py-2 border border-[var(--bdr)] text-[#999] rounded-lg text-xs font-medium hover:text-[var(--t1)] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={14} /> Back
          </button>

          <div className="flex-1" />

          {/* Save and finish later — appears once step 1 is valid AND we're past step 1
              (i.e. the user has chosen to keep going but might want to bail) */}
          {onSaveMinimum && isStep1Valid && stepIdx > 0 && !isLastStep && (
            <button
              type="button"
              onClick={handleSaveMinimum}
              disabled={submitting}
              className="px-3 py-2 text-xs font-medium border border-[var(--bdr)] text-[#aaa] hover:text-[var(--t1)] rounded-lg disabled:opacity-50"
              title="Save with what you've entered so far. You can fill the rest later."
            >
              Save & finish later
            </button>
          )}

          {/* Save and finish later — also visible right at step 1 once it validates,
              so users who only want the minimum can ship without ever clicking Continue */}
          {onSaveMinimum && isStep1Valid && stepIdx === 0 && (
            <button
              type="button"
              onClick={handleSaveMinimum}
              disabled={submitting}
              className="px-3 py-2 text-xs font-medium border border-[var(--bdr)] text-[#aaa] hover:text-[var(--t1)] rounded-lg disabled:opacity-50"
              title="Create with just the essentials. Fill the rest later."
            >
              {submitting ? "Saving…" : "Save & finish later"}
            </button>
          )}

          {!isLastStep ? (
            <button
              type="button"
              onClick={goNext}
              disabled={submitting}
              className="flex items-center gap-1.5 px-4 py-2 bg-[var(--r)] text-white rounded-lg text-xs font-bold hover:opacity-90 disabled:opacity-50"
            >
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSaveFull}
              disabled={submitting}
              className="flex items-center gap-1.5 px-4 py-2 bg-[var(--r)] text-white rounded-lg text-xs font-bold hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? "Saving…" : finalLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
